import { ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useConversionTracking } from '@/hooks/useMarketingTracking';
import { CALCOM_TRIAL_CLASS_URL, CALCOM_BASE_URL } from '@/lib/constants';

interface TrackedBookingLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export default function TrackedBookingLink({
  href,
  children,
  className,
  target,
  rel,
  onClick,
}: TrackedBookingLinkProps) {
  const { trackTrialBooking } = useConversionTracking();
  const location = useLocation();

  // Check if href is a booking link that should go to Cal.com
  const isBookingLink = href === '/#book-trial' || href.includes('#book-trial');

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    trackTrialBooking();
    
    // Skip scroll logic for external links (target="_blank") or booking links that go to Cal.com
    const shouldSkipScroll = target === '_blank' || (isBookingLink && !href.startsWith('http'));
    
    // Handle anchor links only if not skipping scroll
    if (!shouldSkipScroll && href.includes('#')) {
      const [path, hash] = href.split('#');
      const targetId = hash;
      const currentPath = location.pathname === '/' ? '' : location.pathname;
      const normalizedPath = path === '/' ? '' : path;
      const isSamePage = currentPath === normalizedPath;

      if (isSamePage) {
        e.preventDefault();
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const offset = 80; // Account for fixed header
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = targetElement.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
    
    onClick?.(e as React.MouseEvent<HTMLAnchorElement>);
  };

  const isExternal = href.startsWith('http') || target === '_blank' || isBookingLink;

  if (isExternal) {
    // If it's a booking link, use Cal.com URL
    let finalHref = href;
    if (isBookingLink && !href.startsWith('http')) {
      // If target is _blank, use base URL to show all classes
      // Otherwise use trial class URL for specific booking
      finalHref = (target === '_blank' ? CALCOM_BASE_URL : CALCOM_TRIAL_CLASS_URL) || href;
    }
    
    return (
      <a
        href={finalHref}
        target={isBookingLink ? '_blank' : target}
        rel={rel || (isBookingLink || target === '_blank' ? 'noopener noreferrer' : undefined)}
        className={className}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={href}
      className={className}
      onClick={handleClick as React.MouseEventHandler<HTMLAnchorElement>}
    >
      {children}
    </Link>
  );
}


